import { useEffect, useState } from "react";

import noop from "lodash/noop";
import { List } from "react-virtualized";

import { translate } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";

import { CompetitorsStepKeys } from "../../dataAutomationKeys";
import { WebsiteListItem } from "../../WebsiteListItem/WebsiteListItem";
import { EMPTY_STATE_IMAGE_SRC, LIST_PARAMS, MIN_VIEWPORT_HEIGHT } from "../constants";
import { competitorsStepTexts } from "../texts";
import type { CompetitorsMap, CompetitorSchema } from "../types";

import { MIN_COUNT_COMPETITORS_TO_SCROLL } from "./constants";
import { animateScroll } from "./helpers";
import { EmptyStateContainer, EmptyStateImage, EmptyStateSubText, EmptyStateText } from "./styles";
import * as SC from "./styles";

type CompetitorsListSchema = {
    competitorsList: CompetitorsMap;
    showWebsiteSelect: boolean;
    selectedCompetitors: CompetitorSchema[];
    onSelectCompetitor: (competitor: CompetitorSchema) => void;
    isLimitReached: boolean;
    isBigList: boolean;
    shouldScroll: boolean;
};

const CompetitorsList = ({
    competitorsList,
    selectedCompetitors,
    onSelectCompetitor,
    showWebsiteSelect,
    isLimitReached,
    isBigList,
    shouldScroll,
}: CompetitorsListSchema) => {
    const [listScroll, setListScroll] = useState(0);
    const [isSmallHeight, setIsSmallHeight] = useState(false);

    const getCompetitorsList = ({ index, key, style }) => {
        const keys = Array.from(competitorsList.keys());
        const currentKey = keys[index];

        const { name, image, reason, isAiGenerated } = competitorsList.get(currentKey);

        const isSelected = selectedCompetitors.find((competitor) => competitor.name === name);
        const isDisabled = isLimitReached && !isSelected;

        return (
            <div
                style={style}
                key={key}
                data-automation={CompetitorsStepKeys.competitorsListItem}
            >
                <SC.ItemWrapper>
                    <WebsiteListItem
                        isSelected={!!isSelected}
                        isDisabled={isDisabled}
                        suggestedWebsite={{
                            name,
                            image,
                            displayText: name,
                            isAiGenerated,
                            reason,
                        }}
                        suggestionReason={reason}
                        isAISuggestion={isAiGenerated}
                        isMaxSelected={isLimitReached}
                        onClick={onSelectCompetitor}
                    />
                </SC.ItemWrapper>
            </div>
        );
    };

    const handleScroll = ({ scrollTop }) => {
        setListScroll(scrollTop);
    };

    useEffect(() => {
        const screenHeight = window.innerHeight;

        setIsSmallHeight(screenHeight <= MIN_VIEWPORT_HEIGHT);
    }, []);

    useEffect(() => {
        const competitorsSize = selectedCompetitors?.length || 0;

        if (
            shouldScroll &&
            competitorsList?.size &&
            competitorsSize &&
            competitorsSize >= MIN_COUNT_COMPETITORS_TO_SCROLL
        ) {
            animateScroll({
                fromValue: 0,
                toValue: LIST_PARAMS.rowHeight * competitorsSize,
                onUpdate: (scrollTop: number) => setListScroll(scrollTop),
                onComplete: noop,
            });
        }
    }, [competitorsList]);

    const getListHeight = () => {
        if (isBigList) {
            return LIST_PARAMS.bigListHeight;
        }

        return isSmallHeight ? LIST_PARAMS.smallListHeight : LIST_PARAMS.height;
    };

    return (
        <div data-automation={CompetitorsStepKeys.competitorsListWrapper}>
            {competitorsList?.size ? (
                <List
                    width={LIST_PARAMS.width}
                    rowHeight={LIST_PARAMS.rowHeight}
                    height={getListHeight()}
                    rowCount={competitorsList?.size}
                    rowRenderer={getCompetitorsList}
                    onScroll={handleScroll}
                    scrollTop={listScroll}
                />
            ) : (
                <EmptyStateContainer data-automation={CompetitorsStepKeys.emptyState}>
                    <EmptyStateImage src={AssetsService.assetUrl(EMPTY_STATE_IMAGE_SRC)} />
                    <EmptyStateText>
                        {translate(
                            showWebsiteSelect
                                ? competitorsStepTexts.aiEmptyCompetitorsNoPrimary
                                : competitorsStepTexts.emptyCompetitors,
                        )}
                    </EmptyStateText>
                    {showWebsiteSelect ? (
                        <EmptyStateSubText>
                            {translate(competitorsStepTexts.aiEmptyCompetitorsNoPrimarySubMessage)}
                        </EmptyStateSubText>
                    ) : null}
                </EmptyStateContainer>
            )}
        </div>
    );
};

export default CompetitorsList;
